/**
 * FO6 Structure Loss Settlement and Roof Loss Settlement Business Logic Utilities
 *
 * This file contains all business logic for FO-6 Structures Loss Settlement options
 * and Roof Loss Settlement options, including structure category constraints and
 * access control (both role-based and age-based).
 *
 * Business Rules Implementation:
 *
 * Structure Category Rules:
 * - (a) Confinement Buildings: RC and ACV options for both Structure and Roof Loss Settlement
 * - (b) Dwelling and Contents: ACV only for both
 * - (c) Fencing: ACV only for Structure Loss Settlement, no Roof Loss Settlement field
 * - (d) Hoop Buildings: ACV only for both
 * - (e) Poultry Buildings: Age-based RC (same as confinement), role-based fallback for older buildings
 * - (f) Silos and Grain Bins: underwriters see RC options, everyone else sees ACV only
 * - (g) Barns/Outbuildings: Building class logic (1/2=RC, 3=ACV)
 *
 * Role-Based Access Control:
 * - Poultry and Silos/Grain Bins: Agents get ACV only, Underwriters get both ACV and RC
 *
 * Age-Based Access Control:
 * - Confinement Buildings: RC available when building age ≤ configured threshold (default 15 years)
 *   - Available to all agents (independent and staff) when age criteria are met
 *   - Buildings older than threshold or with unknown age: ACV only
 * - Poultry Buildings: Same age-based logic as confinement buildings
 *   - Buildings ≤ threshold: RC available to all agents
 *   - Buildings older than threshold or with unknown age: role-based fallback (underwriter-only RC)
 *
 * Interdependency Rules:
 * - When Structure Loss Settlement is ACV, Roof Loss Settlement must also be ACV
 * - When Structure Loss Settlement is RC, Roof Loss Settlement can be ACV or RC
 *
 * Related GitHub Issues:
 * - Original implementation: #28408
 * - Structure category validation: #28408v2
 * - Age-based RC for confinement buildings: #28945
 *
 * @file FO6.utils.js
 * @version 2.0
 */

// #31836: minimum Coverage E an underwriter may set on FO6 ACV items, overriding the
// 80% ACV floor. Mirrors UW_ACV_COVERAGE_E_FLOOR in common/helpers/yup-helpers.js (the
// Yup submission path) so inline and submit validation agree.
const UW_ACV_COVERAGE_E_FLOOR = 1000;

/**
 * Validates 80% replacement cost requirement for RC selections
 * @param {Object} params - Parameters object
 * @param {Object} params.coverages - Coverage data
 * @param {Object} params.building - Building data with replacementCostEstimate
 * @param {boolean} [params.isNewRisk=false] - Whether this is a new (unrated) risk
 * @returns {Object} Validation result with isValid and message
 */
export const validate80PercentRC = ({ coverages, building, isNewRisk = false }) => {
  // NONE (roof excluded) doesn't require 80% RC validation
  if (coverages?.roofLossSettlement === 'NONE') {
    return { isValid: true, message: '' };
  }

  if (
    coverages?.structureLossSettlement === 'RC' ||
    coverages?.roofLossSettlement === 'RC' ||
    coverages?.roofLossSettlement === 'RC_ACV_WIND_HAIL'
  ) {
    const actualReplacementCost = building?.replacementCostEstimate?.total || 0;

    // For new risks, allow RC selection without RCE validation
    if (isNewRisk && actualReplacementCost === 0) {
      return {
        isValid: true,
        message: '✓ RC loss settlement selected. Validation will apply after rating.'
      };
    }

    // Existing validation logic for rated risks
    if (actualReplacementCost > 0) {
      const coverageE = coverages?.coverageE || 0;
      const minimumRequired = actualReplacementCost * 0.8;

      if (coverageE < minimumRequired) {
        return {
          isValid: false,
          message: `Coverage E must be at least 80% of replacement cost ($${minimumRequired.toLocaleString()}) when RC is selected. Current RCE: $${actualReplacementCost.toLocaleString()}.`
        };
      }

      return {
        isValid: true,
        message: `✓ Coverage E meets 80% RC requirement ($${minimumRequired.toLocaleString()} minimum).`
      };
    }

    // Only show error for existing risks without RCE data
    return {
      isValid: false,
      message: 'RC loss settlement selected but RCE data not available for validation.'
    };
  }

  return { isValid: true, message: '' };
};

/**
 * Validates 80% actual cash value requirement for ACV loss settlement selections.
 * For ACV buildings, Coverage E must be at least 80% of ACV.
 * Related issues: #28950, #28923
 *
 * @param {Object} params - Parameters object
 * @param {Object} params.coverages - Coverage data
 * @param {Object} params.building - Building data with actualCashValueCalculation
 * @param {boolean} [params.isNewRisk=false] - Whether this is a new (unrated) risk
 * @returns {Object} Validation result with isValid and message
 */
export const validate80PercentACV = ({ coverages, building, isNewRisk = false, isUnderwriter: isUW = false }) => {
  if (coverages?.structureLossSettlement !== 'ACV') {
    return { isValid: true, message: '' };
  }

  const acvTotal = building?.actualCashValueCalculation?.total || 0;

  // For new risks without ACV data, allow selection without validation
  if (isNewRisk && acvTotal === 0) {
    return { isValid: true, message: '' };
  }

  if (acvTotal > 0) {
    const coverageE = coverages?.coverageE || 0;
    const eightyPercentAcv = Math.round(acvTotal * 0.8);
    // #31836: underwriters may lower FO6 ACV Coverage E to a $1,000 floor; agents are
    // held to 80% of ACV. Keep this inline validation in step with the Yup submission
    // floor (rc80PercentRequirement) so the field doesn't show a red error for UW.
    const minimumRequired = isUW ? Math.min(UW_ACV_COVERAGE_E_FLOOR, eightyPercentAcv) : eightyPercentAcv;

    if (coverageE < minimumRequired) {
      return {
        isValid: false,
        message: isUW
          ? `Coverage E must be at least $${minimumRequired.toLocaleString()}.`
          : `Coverage E must be at least 80% of ACV ($${minimumRequired.toLocaleString()}). Current ACV: $${acvTotal.toLocaleString()}.`
      };
    }

    return {
      isValid: true,
      message: `Coverage E meets 80% ACV requirement ($${minimumRequired.toLocaleString()} minimum).`
    };
  }

  return { isValid: true, message: '' };
};

/**
 * Role-based access control helper - checks if user is underwriter
 * @param {Object} session - User session object
 * @returns {boolean} Whether user has underwriter role
 */
export const isUnderwriter = (session) => {
  if (!session?.user) {
    return false;
  }

  const role = session.user.agencyRole?.toLowerCase();
  // Fix: Use Array.isArray() to safely handle null/undefined permissionGroups
  const groups = Array.isArray(session.user.permissionGroups)
    ? session.user.permissionGroups.map((g) => g?.toLowerCase?.() || '').filter(Boolean)
    : [];

  return role === 'underwriter' || groups.includes('underwriter');
};

/**
 * Role-based access control helper - checks if user is agent
 * @param {Object} session - User session object
 * @returns {boolean} Whether user has agent role
 */
/**
 * Gets role-based loss settlement options for structure types requiring role-based access control
 * @param {string} structureType - Type of structure ('storageGrain', 'confinement', 'poultry')
 * @param {Object} session - User session object
 * @returns {Array} Array of loss settlement options based on user role
 */
export const getRoleBasedLossSettlementOptions = (structureType, session) => {
  // Default to most restrictive permissions if no session
  if (!session) {
    return [{ id: 'ACV', value: 'Actual cash value' }];
  }

  // Check user roles - functions already have null safety
  if (isAgent(session)) {
    // Agents: ACV only for all restricted structure types
    return [{ id: 'ACV', value: 'Actual cash value' }];
  }

  if (isUnderwriter(session)) {
    // Underwriters: Both ACV and RC options
    const rcLabel =
      structureType === 'confinement' || structureType === 'poultry'
        ? 'Replacement cost (Requires UW Approval)'
        : 'Replacement cost';

    return [
      { id: 'ACV', value: 'Actual cash value' },
      { id: 'RC', value: rcLabel }
    ];
  }

  // Fallback for other roles (admins, super-users, etc.)
  // Default to most restrictive permissions for safety
  return [{ id: 'ACV', value: 'Actual cash value' }];
};

/**
 * Gets loss settlement options based on structure type, barn type, and user session
 *
 * This is the main entry point for structure loss settlement options, implementing
 * all business rules for different structure categories and role-based access control.
 *
 * Structure Category Business Rules:
 * - Dwelling/Contents, Hoop Buildings, Fencing: Always ACV only (no RC allowed)
 * - Silos/Grain Bins: Role-based (Agents get ACV only, Underwriters get both)
 * - Confinement Buildings: Age-based (buildings ≤ maxAgeThreshold get both ACV and RC, older buildings get ACV only)
 * - Poultry Buildings: Age-based (buildings ≤ maxAgeThreshold get both ACV and RC), role-based fallback for older
 * - Barns/Outbuildings: Building class determines options (1/2=RC only, 3=ACV only)
 *
 * @param {Object} params - Parameters object
 * @param {boolean} params.isDwellingContents - Whether structure is dwelling/contents
 * @param {boolean} params.isHoopBuilding - Whether structure is hoop building
 * @param {boolean} params.isFencing - Whether structure is fencing
 * @param {boolean} params.isStorageGrain - Whether structure is storage/grain (Silos/Grain Bins)
 * @param {boolean} params.isConfinementBuilding - Whether structure is confinement building
 * @param {boolean} params.isPoultryBuilding - Whether structure is poultry building
 * @param {boolean} params.isBarnOutbuilding - Whether structure is barn/outbuilding
 * @param {number|null} params.buildingClass - Building class (1, 2, 3) for barn/outbuilding structures
 * @param {Object} params.session - User session object containing role information
 * @param {number|null} params.buildingAge - Age of the building in years (for confinement and poultry buildings)
 * @param {number} params.maxAgeThreshold - Maximum age threshold for RC eligibility (for confinement and poultry buildings)
 * @returns {Array} Array of loss settlement options with id/value pairs
 */
export const getLossSettlementOptions = ({
  isDwellingContents,
  isHoopBuilding,
  isFencing,
  isStorageGrain,
  isConfinementBuilding,
  isPoultryBuilding,
  isBarnOutbuilding,
  buildingClass,
  session,
  buildingAge = null,
  maxAgeThreshold = 15
}) => {
  // Business Rule (b), (d), (c): Dwelling/Contents, Hoop Buildings, Fencing always ACV only
  // These structure types do not qualify for Replacement Cost coverage under any circumstances
  if (isDwellingContents || isHoopBuilding || isFencing) {
    return [{ id: 'ACV', value: 'Actual cash value' }];
  }

  // Business Rule (f): Silos/Grain Bins - Role-based access control
  // Underwriters can offer RC, Agents restricted to ACV only
  if (isStorageGrain) {
    return getRoleBasedLossSettlementOptions('storageGrain', session);
  }

  // Business Rule (a): Confinement Buildings - Age-based access control
  // Buildings ≤ maxAgeThreshold years old can have RC, older buildings restricted to ACV only
  // Available to all agents (independent and staff) when age criteria is met
  if (isConfinementBuilding) {
    // If building age is not provided or building is older than threshold, ACV only
    if (buildingAge === null || buildingAge > maxAgeThreshold) {
      return [{ id: 'ACV', value: 'Actual cash value' }];
    }

    // Building is ≤ maxAgeThreshold years old, offer both ACV and RC (ACV is default)
    return [
      { id: 'ACV', value: 'Actual cash value' },
      { id: 'RC', value: 'Replacement cost' }
    ];
  }

  // Business Rule (e): Poultry Buildings - Age-based access control with role-based fallback
  // Buildings ≤ maxAgeThreshold years old can have RC (available to all agents)
  // Older buildings or unknown age: fall back to role-based (underwriter-only RC)
  if (isPoultryBuilding) {
    if (buildingAge !== null && buildingAge <= maxAgeThreshold) {
      return [
        { id: 'ACV', value: 'Actual cash value' },
        { id: 'RC', value: 'Replacement cost' }
      ];
    }
    return getRoleBasedLossSettlementOptions('poultry', session);
  }

  // Business Rule (g): Barns/Outbuildings - Building Class determines coverage options
  // This logic overrides role-based access control for barn structures
  if (isBarnOutbuilding) {
    if (!buildingClass) {
      // Building class not yet determined - show both options to avoid blocking user
      // Once building characteristics are filled, proper restrictions will apply
      return [
        { id: 'ACV', value: 'Actual cash value' },
        { id: 'RC', value: 'Replacement cost' }
      ];
    }

    if (buildingClass === 1 || buildingClass === 2) {
      // Building Class 1 & 2: Must use RC (required for special Building Class 1 coverages)
      // For Building Class 1 & 2, spell out "Replacement Cost" in full (not abbreviated as "RC")
      return [{ id: 'RC', value: 'Replacement cost' }];
    }

    if (buildingClass === 3) {
      // Building Class 3: ACV only (older buildings not eligible for RC)
      return [{ id: 'ACV', value: 'Actual cash value' }];
    }

    // Fallback for invalid building class values
    return [{ id: 'ACV', value: 'Actual cash value' }];
  }

  // Default for other structure types
  return [
    { id: 'RC', value: 'Replacement cost' },
    { id: 'ACV', value: 'Actual cash value' },
    { id: 'FUNC', value: 'Functional replacement cost' }
  ];
};

/**
 * Get roof loss settlement options for FO-6 structures
 *
 * GitHub issue #29142: Universal rule - Structure=RC → Roof=RC_ACV_WIND_HAIL, Structure=ACV → Roof=ACV
 * Unless exclusion/restriction area updates the roof loss settlement.
 *
 * @param {Object} params - Parameters object
 * @param {string} structureLossSettlement - Current structure loss settlement value
 * @param {number} buildingClass - Building class (1, 2, 3) for barn/outbuilding structures
 * @param {boolean} isBarnOutbuilding - Whether structure is barn/outbuilding type
 * @param {string} roofExclusionRestriction - Roof exclusion/restriction value ('excluded', 'restricted', or null)
 * @returns {Array} Available roof loss settlement options
 */
export const getRoofLossSettlementOptions = ({
  structureLossSettlement,
  buildingClass,
  isBarnOutbuilding,
  roofExclusionRestriction
}) => {
  // For barn outbuildings, prioritize building class logic over current structure settlement
  // This prevents timing issues during class transitions (e.g., 3→1 where buildingClass updates before structureLossSettlement)
  if (isBarnOutbuilding && buildingClass) {
    if (buildingClass === 3) {
      // Building Class 3 barns: ACV only (structure is always ACV for class 3)
      return [{ id: 'ACV', value: 'Actual cash value' }];
    }

    if (buildingClass === 1 || buildingClass === 2) {
      // GitHub issue #29142: Building Class 1 & 2 barns with RC structure → RC_ACV_WIND_HAIL only
      const options = [{ id: 'RC_ACV_WIND_HAIL', value: 'Replacement cost with ACV for wind/hail' }];

      // Add "None" option when roof exclusion/restriction is configured
      // Use 'NONE' as the canonical value (matches backend post-data-rules)
      if (roofExclusionRestriction === 'excluded') {
        options.push({ id: 'NONE', value: 'Excluded' });
      }

      return options;
    }
  }

  // GitHub issue #29142: Structure=RC → Roof=RC_ACV_WIND_HAIL only
  // Universal rule for all building types (confinement, poultry, silos, etc.)
  if (structureLossSettlement === 'RC') {
    const options = [{ id: 'RC_ACV_WIND_HAIL', value: 'Replacement cost with ACV for wind/hail' }];
    // Add Excluded option when roof exclusion/restriction is configured (#29139)
    if (roofExclusionRestriction === 'excluded') {
      options.push({ id: 'NONE', value: 'Excluded' });
    }
    return options;
  }

  // Structure=ACV or any other case → ACV only
  const options = [{ id: 'ACV', value: 'Actual cash value' }];
  if (roofExclusionRestriction === 'excluded') {
    options.push({ id: 'NONE', value: 'Excluded' });
  }
  return options;
};

/**
 * Validate roof loss settlement business rules
 * @param {Object} coverages - Coverage values object
 * @returns {Object} Validation result { isValid: boolean, message: string }
 */
export const validateRoofLossSettlement = (coverages) => {
  const { structureLossSettlement, roofLossSettlement } = coverages || {};

  // NONE (roof excluded) is always valid
  if (roofLossSettlement === 'NONE') {
    return { isValid: true, message: '' };
  }

  // Rule: When structure is ACV, roof cannot be RC or RC_ACV_WIND_HAIL
  if (structureLossSettlement === 'ACV' && (roofLossSettlement === 'RC' || roofLossSettlement === 'RC_ACV_WIND_HAIL')) {
    return {
      isValid: false,
      message:
        'Roof loss settlement cannot be Replacement Cost (including RC with ACV for Wind/Hail) when structure loss settlement is ACV'
    };
  }

  // Rule: When structure is RC, roof must be RC_ACV_WIND_HAIL only (per GitHub issue #29142)
  if (structureLossSettlement === 'RC' && roofLossSettlement !== 'RC_ACV_WIND_HAIL') {
    return {
      isValid: false,
      message:
        'Roof loss settlement must be RC with ACV for Wind/Hail (not plain Replacement Cost) when structure loss settlement is RC'
    };
  }

  return { isValid: true, message: '' };
};

export const isAgent = (session) => {
  if (!session?.user) {
    return false; // Deny access if no session
  }

  const role = session.user.agencyRole?.toLowerCase();
  return role === 'agent';
};

/**
 * Calculate building age from year built
 * @param {string|number} yearBuilt - Year the building was built
 * @returns {number|null} Building age in years, or null if yearBuilt is invalid
 */
export const getConfinementBuildingAge = (yearBuilt) => {
  if (!yearBuilt) {
    return null;
  }

  const currentYear = new Date().getFullYear();
  const yearBuiltNum = parseInt(yearBuilt, 10);

  if (isNaN(yearBuiltNum) || yearBuiltNum < 1800 || yearBuiltNum > currentYear) {
    return null;
  }

  return currentYear - yearBuiltNum;
};

/**
 * Validates roof year against business rules
 * @param {string|number} roofYear - Roof year value
 * @param {string|number} yearBuilt - Building year built value
 * @returns {Object} Validation result with isValid and message
 */
export const validateRoofYear = (roofYear, yearBuilt) => {
  if (!roofYear) {
    return { isValid: true, message: '' };
  }

  const currentYear = new Date().getFullYear();
  const roofYearNum = parseInt(roofYear, 10);
  const yearBuiltNum = parseInt(yearBuilt, 10);

  // Check for future year
  if (roofYearNum > currentYear) {
    return {
      isValid: false,
      message: `Roof year cannot be in the future (max: ${currentYear})`
    };
  }

  // Check against year built
  if (yearBuilt && roofYearNum < yearBuiltNum) {
    return {
      isValid: false,
      message: `Roof year cannot be before building was built (${yearBuilt})`
    };
  }

  return { isValid: true, message: '' };
};
